import api from '../api'
import { Address, Employee, Organization, SCHEDULE_DAY_STATUS_KEYS, UpdateOrganizationPayload } from '../../types/settings/organizationTypes'

/** API address shape (snake_case, line_1/postal_code) */
interface ApiAddress {
  line_1?: string
  line_2?: string
  postal_code?: string
  city?: string
  country?: string
}

/** Raw organization item from API (data array item) */
interface ApiOrganizationRow {
  id: string
  logo: string | null
  legal_name?: string
  display_name?: string
  url?: string
  email?: string
  phone_number?: string
  pilot_start?: string | null
  license_start?: string | null
  postal_address?: ApiAddress
  visiting_address?: ApiAddress
  vat_number?: string
  invoice_address?: ApiAddress | Record<string, unknown>
  primary_contact?: unknown
  secondary_contact?: unknown
  tertiary_contact?: unknown
  invoice_contact?: unknown
  invite_token?: string
  created_at?: string
  updated_at?: string
  used_input_tokens?: number
  used_output_tokens?: number
  mfa_required?: boolean
  show_pending_requests_on_calendar?: boolean
  enable_office_arrival_reminders?: boolean
  enable_weekend_status?: boolean
  /** Backed by a JSONField, so the API can hand back any JSON shape, not just a list. */
  schedule_day_statuses?: unknown
  entra_tenant_id?: string
  entra_anchor_upn?: string
  entra_consent_granted_at?: string | null
}

function mapApiAddressToAddress(raw?: ApiAddress | Record<string, unknown> | null): Address {
  if (!raw || typeof raw !== 'object') {
    return { address1: '', address2: '', city: '', zipCode: '', country: '' }
  }
  const a = raw as ApiAddress
  return {
    address1: a.line_1 ?? '',
    address2: a.line_2 ?? '',
    city: a.city ?? '',
    zipCode: a.postal_code ?? '',
    country: a.country ?? '',
  }
}

/** Normalize contact from API: may be id string or full object */
function mapContact(raw: unknown): Employee | null {
  if (raw == null) return null
  if (typeof raw === 'string') {
    return { id: raw, first_name: '', last_name: '', work_email: '' }
  }
  if (typeof raw === 'object' && 'id' in raw) {
    const o = raw as Record<string, unknown>
    return {
      id: String(o.id ?? ''),
      first_name: String(o.first_name ?? ''),
      last_name: String(o.last_name ?? ''),
      work_email: String(o.work_email ?? ''),
      phone_number: o.phone_number != null ? String(o.phone_number) : undefined,
      profile_picture: o.profile_picture != null ? (o.profile_picture as string) : undefined,
    }
  }
  return null
}

/** schedule_day_statuses is an unvalidated JSONField server-side, so a row can hold a
 * JSON-encoded list ('["remote"]'), a comma string, or — from the spread-a-string bug
 * this replaced — a list of single characters. Callers index it as a string[] and the
 * settings picker writes it straight back, so "is a list of strings" is not enough:
 * a character list survives that test, grows by one element per click, and is refused
 * by the backend validator. Only real status keys get through, so a corrupted row
 * reads as "nothing selected" and the next click rewrites it clean. */
function normalizeScheduleDayStatuses(raw: unknown): string[] {
  const known = new Set<string>(SCHEDULE_DAY_STATUS_KEYS)
  /** Known keys only, first occurrence wins, original order preserved. */
  const keep = (values: unknown[]): string[] => {
    const kept: string[] = []
    for (const value of values) {
      if (typeof value === 'string' && known.has(value) && !kept.includes(value)) kept.push(value)
    }
    return kept
  }

  if (Array.isArray(raw)) return keep(raw)
  if (typeof raw !== 'string') return []
  const trimmed = raw.trim()
  if (!trimmed) return []
  try {
    const parsed: unknown = JSON.parse(trimmed)
    if (Array.isArray(parsed)) return keep(parsed)
    // Valid JSON but not a list: only a quoted string names a status, and any
    // other scalar ('null', '42', '{}') carries nothing we can offer.
    return typeof parsed === 'string' ? keep([parsed]) : []
  } catch {
    // Not JSON — fall through and read it as a comma-separated list.
  }
  return keep(trimmed.split(',').map((value) => value.trim()))
}

function mapApiOrganizationToOrganization(raw: ApiOrganizationRow): Organization {
  return {
    id: raw.id,
    logo: raw.logo ?? null,
    legal_name: raw.legal_name ?? '',
    display_name: raw.display_name ?? '',
    url: raw.url ?? '',
    email: raw.email ?? '',
    phone_number: raw.phone_number ?? '',
    pilot_start: raw.pilot_start ?? null,
    license_start: raw.license_start ?? null,
    postal_address: mapApiAddressToAddress(raw.postal_address),
    visiting_address: mapApiAddressToAddress(raw.visiting_address),
    vat_number: raw.vat_number ?? '',
    invoice_address: mapApiAddressToAddress(raw.invoice_address),
    primary_contact: mapContact(raw.primary_contact),
    secondary_contact: mapContact(raw.secondary_contact),
    tertiary_contact: mapContact(raw.tertiary_contact),
    invoice_contact: mapContact(raw.invoice_contact),
    invite_token: raw.invite_token,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
    used_input_tokens: raw.used_input_tokens,
    used_output_tokens: raw.used_output_tokens,
    mfa_required: raw.mfa_required,
    show_pending_requests_on_calendar: raw.show_pending_requests_on_calendar,
    enable_office_arrival_reminders: raw.enable_office_arrival_reminders,
    enable_weekend_status: raw.enable_weekend_status,
    schedule_day_statuses: normalizeScheduleDayStatuses(raw.schedule_day_statuses),
    entra_tenant_id: raw.entra_tenant_id ?? '',
    entra_anchor_upn: raw.entra_anchor_upn ?? '',
    entra_consent_granted_at: raw.entra_consent_granted_at ?? null,
  }
}

export interface OrganizationListAllItem {
  id: string
  legal_name: string
  display_name: string
  logo: string | null
  subscription_plan: string | null
  subscription_status: string | null
  subscription_current_period_end: string | null
  has_stripe_customer: boolean
  employee_count: number
  billing_exempt: boolean
  created_at: string
}

export const getOrganizationListAll = async ({
  search = '',
  page = 1,
  pageSize = 15,
  subscription_plan,
  subscription_status,
  fields,
}: {
  search?: string
  page?: number
  pageSize?: number
  subscription_plan?: string[]
  subscription_status?: string[]
  fields?: string
}): Promise<{ data: OrganizationListAllItem[]; count: number; home_organization_id: string }> => {
  const params: Record<string, string | number> = { search, page, page_size: pageSize }
  if (subscription_plan?.length) params.subscription_plan = subscription_plan.join(',')
  if (subscription_status?.length) params.subscription_status = subscription_status.join(',')
  if (fields) params.fields = fields
  const response = await api.get('/organization/list-all/', { params })
  return response.data
}

export interface OrganizationListItem {
  id: string
  legal_name: string
  display_name: string
  logo: string | null
}

export const getOrganizationList = async (): Promise<OrganizationListItem[]> => {
  const response = await api.get<{ data?: OrganizationListItem[] }>('/organization/', {
    params: { fields: 'id,legal_name,display_name,logo' },
  })
  return response.data?.data ?? []
}

export const switchOrganization = async (organizationId: string): Promise<Organization> => {
  const response = await api.post(`/organization/${organizationId}/switch/`)
  return mapApiOrganizationToOrganization(response.data)
}

export const enableOrganizationMFA = async (enabled: boolean) => {
  const response = await api.post('/organization/enable_mfa/', { mfa_enabled: enabled })
  return response.data
}

export const getOrganizationMFA = async () => {
  const response = await api.get('/organization/mfa_status/')
  return response.data
}

export const disableOrganizationMFA = async (enabled: boolean) => {
  const response = await api.post('/organization/disable_mfa/', { mfa_enabled: enabled })
  return response.data
}

const CONTACT_FIELD_NAMES = [
  'id',
  'first_name',
  'last_name',
  'work_email',
  'phone_number',
  'profile_picture',
]

function contactFieldsParam(prefix: string) {
  return CONTACT_FIELD_NAMES.map((f) => `${prefix}.${f}`).join(',')
}

/** Just enough of the current organization to brand the navigation. Readable by every employee. */
export const getOrganizationBranding = async (): Promise<{ id: string; display_name: string; logo: string | null } | null> => {
  const response = await api.get<{ data?: Array<{ id: string; display_name?: string; logo?: string | null }> }>('/organization/', {
    params: { fields: 'id,display_name,logo' },
  })
  const raw = response.data?.data?.[0]
  return raw ? { id: raw.id, display_name: raw.display_name ?? '', logo: raw.logo ?? null } : null
}

export const getOrganization = async (): Promise<Organization> => {
  const response = await api.get<{ data?: ApiOrganizationRow[] }>('/organization/', {
    params: {
      expand: 'primary_contact,secondary_contact,tertiary_contact,invoice_contact',
      fields: [
        '*',
        contactFieldsParam('primary_contact'),
        contactFieldsParam('secondary_contact'),
        contactFieldsParam('tertiary_contact'),
        contactFieldsParam('invoice_contact'),
      ].join(','),
    },
  })
  const list = response.data?.data
  const raw = Array.isArray(list) && list.length > 0 ? list[0] : null
  if (!raw) {
    throw new Error('Organization response has no data')
  }
  return mapApiOrganizationToOrganization(raw)
}

/** Our Address shape to API address shape for PATCH (backend expects dict with line_1, postal_code, etc.) */
function addressToApiShape(addr: Address): ApiAddress {
  return {
    line_1: addr.address1 ?? '',
    line_2: addr.address2 ?? '',
    postal_code: addr.zipCode ?? '',
    city: addr.city ?? '',
    country: addr.country ?? '',
  }
}

function isAddressFullyEmpty(addr: Address): boolean {
  return [addr.address1, addr.address2, addr.zipCode, addr.city, addr.country].every(
    (v) => v === undefined || v === null || String(v).trim() === ''
  )
}

/** Backend requires non-blank for line_1, line_2, postal_code, city and a valid choice for country. When all fields are empty we skip sending; otherwise send with space for blank text fields and omit country when empty. */
function appendAddressFormData(formData: FormData, key: string, address: Address) {
  if (isAddressFullyEmpty(address)) return
  const api = addressToApiShape(address)
  const nonBlank = (s: string | undefined) => (s ?? '').trim() || ' '
  formData.append(`${key}[line_1]`, nonBlank(api.line_1))
  formData.append(`${key}[line_2]`, nonBlank(api.line_2))
  formData.append(`${key}[postal_code]`, nonBlank(api.postal_code))
  formData.append(`${key}[city]`, nonBlank(api.city))
  const country = (api.country ?? '').trim()
  if (country) formData.append(`${key}[country]`, country)
}

const ADDRESS_KEYS = ['postal_address', 'visiting_address', 'invoice_address'] as const

export const adminResetMfa = async (data: {
  employee_id: string
  action: 'reset' | 'disable'
  clear_phone: boolean
}): Promise<{ logged_out: boolean; custom_token?: string }> => {
  const res = await api.post('/user/mfa-reset/admin/', data)
  return res.data
}

const EMPTY_API_ADDRESS = {
  line_1: '',
  line_2: '',
  postal_code: '',
  city: '',
  country: '',
}

export const createOrganization = async (data: { legal_name: string; display_name: string }): Promise<Organization> => {
  const response = await api.post('/organization/', {
    ...data,
    postal_address: EMPTY_API_ADDRESS,
    visiting_address: EMPTY_API_ADDRESS,
    invoice_address: EMPTY_API_ADDRESS,
  })
  return mapApiOrganizationToOrganization(response.data)
}

export const updateOrganization = async (id: string, data: UpdateOrganizationPayload): Promise<Organization> => {
  // When removing logo, send JSON so backend receives literal null (FormData cannot send null)
  if (data.logo === null) {
    const response = await api.patch(`/organization/${id}/`, { logo: null }, {
      headers: { 'Content-Type': 'application/json' },
    })
    return response.data
  }

  const formData = new FormData()

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    if (ADDRESS_KEYS.includes(key as (typeof ADDRESS_KEYS)[number])) {
      appendAddressFormData(formData, key, value as Address)
      return
    }
    if (typeof value === 'object' && !(value instanceof File)) {
      formData.append(key, JSON.stringify(value))
    } else {
      formData.append(key, value)
    }
  })

  const response = await api.patch(`/organization/${id}/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data
}
